import { useMutation, useQuery } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { InlineSpinner } from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import moment from "moment";
import { useContext, useEffect, useRef, useState } from "react";
import { RiArrowUpDownLine } from "react-icons/ri";
import { v4 } from "uuid";
import {
  FETCH_SALABLE_PRODUCTS_FOR_OS,
  OS_DELETE_PLAN_LINE,
  OS_SAVE_PLAN_LINE,
} from "./graphql";
import { PlanContext } from "./PlanForm";
import PlanLine from "./PlanLine";
import { buildOtherProductSalesPricesMap, computePlanLine } from "./utils";

const LINE_SAVE_DEBOUNCE_MS = 600;

const PlanLines = ({
  originalPlan,
  odooSalesOrderWarehouseId,
  requiredCertificateTypes,
  lines,
  setLines,
  onChangeLine,
  onChangeLineProduct,
  removeLine,
  disabled,
  isComputing,
  reduceQty,
}) => {
  const { currencySymbolString } = useContext(PlanContext);
  const { data, loading, error } = useQuery(FETCH_SALABLE_PRODUCTS_FOR_OS, {
    variables: { planId: originalPlan.id },
    fetchPolicy: "network-only",
  });
  const [savePlanLine] = useMutation(OS_SAVE_PLAN_LINE, {
    onError(err) {
      Alert("error", parseError(err));
    },
  });
  const [deletePlanLine] = useMutation(OS_DELETE_PLAN_LINE, {
    onError(err) {
      Alert("error", parseError(err));
    },
  });

  const [sortBy, setSortBy] = useState("PRODUCT");
  const [savingLineIds, setSavingLineIds] = useState({});
  const [lockingLineIds, setLockingLineIds] = useState({});
  const latestLinesRef = useRef(lines);
  const lineSaveTimersRef = useRef({});

  useEffect(() => {
    latestLinesRef.current = lines;
  }, [lines]);

  useEffect(() => {
    return () => {
      Object.values(lineSaveTimersRef.current).forEach((timer) =>
        clearTimeout(timer),
      );
    };
  }, []);

  if (loading)
    return (
      <div className="relative mt-4">
        <InlineSpinner size={21} text="Loading products..." />
      </div>
    );
  if (error) return <Errors error={error} />;

  const pricelist = data.productionPlan.wisPriceList;
  const pricelistProductIds =
    pricelist?.latestPrices?.map((i) => i.product.id) || [];
  const parentPlanProductSalesPrices = buildOtherProductSalesPricesMap(
    data.productionPlan,
  );
  const computedLines = lines.map((line) =>
    computePlanLine({
      line,
      pricelist,
      pricelistProductIds,
      requiredCertificateTypes: requiredCertificateTypes || "",
      odooSalesOrderWarehouseId,
      parentPlanProductSalesPrices,
      planType: originalPlan.planType,
    }),
  );

  const totalQty = computedLines.reduce((res, line) => res + line.finalQty, 0);
  const totalCartons = computedLines.reduce((res, line) => {
    return res + (line.totalCartons ?? 0);
  }, 0);
  const totalGrossWeight = computedLines.reduce(
    (res, line) => res + (line.totalGrossWeight ?? 0),
    0,
  );
  const totalCbm = computedLines.reduce((res, line) => res + line.totalCbm, 0);
  const totalPrice = computedLines.reduce(
    (res, line) => res + line.totalPrice,
    0,
  );

  const totalCost = computedLines.reduce(
    (res, line) => res + line.totalCost,
    0,
  );

  function addProduct() {
    setLines((prev) => [
      ...prev,
      {
        id: v4(),
        product: null,
        qty: 0,
        reduceQty: 0,
        totalGrossWeight: 0,
        totalCbm: 0,
        totalCost: 0,
        salesPrice: 0,
        discountPercentage: 0,
        isNew: true,
      },
    ]);
  }

  const duplicatedProducts = computedLines
    .filter((i) => i.product)
    .filter(
      (line, index, self) =>
        index !== self.findIndex((t) => t.product.id === line.product.id),
    );

  function getSalesPrice(product, latestPrices) {
    if (!product) return 0;
    const pricelistItem = latestPrices?.find(
      (i) => i.product.id === product.id,
    );

    if (!pricelistItem) return 0;

    return pricelistItem.finalPrice;
  }

  function markLineSaving(lineId, isSaving) {
    setSavingLineIds((prev) => {
      if (isSaving) {
        return { ...prev, [lineId]: true };
      }
      const next = { ...prev };
      delete next[lineId];
      return next;
    });
  }

  function markLineLocking(lineId, isLocking) {
    setLockingLineIds((prev) => {
      if (isLocking) {
        return { ...prev, [lineId]: true };
      }
      const next = { ...prev };
      delete next[lineId];
      return next;
    });
  }

  async function persistPlanLine(line, { lockLine = true } = {}) {
    if (!line?.product?.id) return;

    const currentLineId = line.id;
    let nextLineIds = [];
    markLineSaving(currentLineId, true);
    if (lockLine) markLineLocking(currentLineId, true);

    try {
      const res = await savePlanLine({
        variables: {
          planId: originalPlan.id,
          productId: line.product.id,
          lineId: line.isNew ? undefined : line.id,
          qty: parseInt(line.qty, 10) || 0,
          salesPrice: parseFloat(line.salesPrice) || 0,
          discountPercentage: parseFloat(line.discountPercentage) || 0,
        },
      });

      const savedLine = res?.data?.osSavePlanLine?.planLine;
      if (!savedLine) return;
      nextLineIds = [savedLine.id];

      setLines((prev) =>
        prev.map((prevLine) => {
          if (prevLine.id !== currentLineId) return prevLine;
          return {
            ...prevLine,
            ...savedLine,
            isNew: false,
          };
        }),
      );
    } finally {
      markLineSaving(currentLineId, false);
      nextLineIds.forEach((nextLineId) => markLineSaving(nextLineId, false));
      if (lockLine) {
        markLineLocking(currentLineId, false);
        nextLineIds.forEach((nextLineId) => markLineLocking(nextLineId, false));
      }
    }
  }

  function schedulePersistPlanLine(lineId) {
    if (lineSaveTimersRef.current[lineId]) {
      clearTimeout(lineSaveTimersRef.current[lineId]);
    }

    lineSaveTimersRef.current[lineId] = setTimeout(async () => {
      delete lineSaveTimersRef.current[lineId];
      const currentLine = latestLinesRef.current.find((i) => i.id === lineId);
      if (!currentLine || currentLine.isNew || !currentLine.product?.id) return;
      await persistPlanLine(currentLine, { lockLine: false });
    }, LINE_SAVE_DEBOUNCE_MS);
  }

  async function handleRemoveLine(lineId) {
    if (lineSaveTimersRef.current[lineId]) {
      clearTimeout(lineSaveTimersRef.current[lineId]);
      delete lineSaveTimersRef.current[lineId];
    }

    const targetLine = latestLinesRef.current.find(
      (line) => line.id === lineId,
    );
    if (!targetLine) return;

    if (targetLine.isNew || !targetLine.product?.id) {
      removeLine(lineId);
      return;
    }

    markLineSaving(lineId, true);
    markLineLocking(lineId, true);
    try {
      await deletePlanLine({
        variables: { planId: originalPlan.id, lineIds: [lineId] },
      });
      removeLine(lineId);
    } finally {
      markLineSaving(lineId, false);
      markLineLocking(lineId, false);
    }
  }

  async function handleClearAllProducts() {
    if (!window.confirm("Are you sure to clear all products?")) {
      return;
    }

    Object.values(lineSaveTimersRef.current).forEach((timer) =>
      clearTimeout(timer),
    );
    lineSaveTimersRef.current = {};

    const currentLines = latestLinesRef.current;
    const lineIdsToDelete = currentLines
      .filter((line) => !line.isNew && line.product?.id)
      .map((line) => line.id);

    if (lineIdsToDelete.length === 0) {
      setLines([]);
      return;
    }

    setSavingLineIds((prev) => {
      const next = { ...prev };
      lineIdsToDelete.forEach((lineId) => {
        next[lineId] = true;
      });
      return next;
    });
    setLockingLineIds((prev) => {
      const next = { ...prev };
      lineIdsToDelete.forEach((lineId) => {
        next[lineId] = true;
      });
      return next;
    });

    try {
      await deletePlanLine({
        variables: { planId: originalPlan.id, lineIds: lineIdsToDelete },
      });
      setLines([]);
    } finally {
      setSavingLineIds((prev) => {
        const next = { ...prev };
        lineIdsToDelete.forEach((lineId) => {
          delete next[lineId];
        });
        return next;
      });
      setLockingLineIds((prev) => {
        const next = { ...prev };
        lineIdsToDelete.forEach((lineId) => {
          delete next[lineId];
        });
        return next;
      });
    }
  }

  function handleLineFieldChange(line, field, value) {
    onChangeLine(line.id, field, value);
    if (["qty", "salesPrice", "discountPercentage"].includes(field)) {
      schedulePersistPlanLine(line.id);
    }
  }

  async function handleLineProductChange(line, product) {
    const previousProductId = line.product?.id;
    const nextProductId = product?.id;
    const hasProductChanged = previousProductId !== nextProductId;

    const salesPrice = getSalesPrice(product, pricelist?.latestPrices);
    onChangeLineProduct(line.id, product, salesPrice);

    if (!hasProductChanged || !nextProductId) return;

    await persistPlanLine(
      {
        ...line,
        product,
        salesPrice,
      },
      { lockLine: true },
    );
  }

  return (
    <div className="-mx-2">
      <table>
        <thead>
          <tr className="whitespace-nowrap">
            <th>
              <div className="flex items-center space-x-1">
                <span>Product</span>
                <Button onClick={() => setSortBy("PRODUCT")}>
                  <RiArrowUpDownLine />
                </Button>
              </div>
            </th>
            <th className="text-right w-12">QTY / CTN</th>
            <th className="text-right w-20">QTY</th>
            <th className="text-right w-20">Total CTN</th>
            <th className="text-right w-20">G.W.</th>
            <th className="text-right w-20">CBM</th>
            <th className="text-right w-28">
              <div className="flex items-center justify-end space-x-1">
                <Button onClick={() => setSortBy("READY_DATE")}>
                  <RiArrowUpDownLine />
                </Button>
                <span>Ready Date</span>
              </div>
            </th>
            {originalPlan.planType === "AB_INTERNAL_TRANSFER" ? (
              <>
                <th className="text-right w-20">Unit Cost</th>
                <th className="text-right w-28">Total Cost</th>
              </>
            ) : (
              <>
                <th className="text-right w-20">Unit Price</th>
                <th className="text-right w-20">Disc %</th>
                <th className="text-right w-20">
                  Total ({originalPlan.wisPriceList?.currency})
                </th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {computedLines
            .sort((a, b) => {
              if (sortBy === "READY_DATE") {
                return new Date(a.readyDate) - new Date(b.readyDate);
              }
              return a.id - b.id;
            })
            .map((line, index) => (
              <PlanLine
                index={index}
                key={line.id}
                line={line}
                onChangeLine={(_, field, value) =>
                  handleLineFieldChange(line, field, value)
                }
                onChangeLineProduct={(product) =>
                  handleLineProductChange(line, product)
                }
                removeLine={handleRemoveLine}
                disabled={disabled}
                isComputing={isComputing}
                isSaving={Boolean(savingLineIds[line.id])}
                isLocking={Boolean(lockingLineIds[line.id])}
              />
            ))}
        </tbody>

        <tbody>
          {setLines && (
            <tr>
              <td
                colSpan={
                  originalPlan.planType === "AB_INTERNAL_TRANSFER" ? 7 : 8
                }
              >
                <div className="flex items-center space-x-4">
                  <Button
                    disabled={
                      disabled || originalPlan.state !== "DRAFT" || isComputing
                    }
                    onClick={addProduct}
                  >
                    + Add Product
                  </Button>

                  {originalPlan.state === "PROCESSING" && reduceQty ? (
                    <Button onClick={reduceQty}>Reduce QTY</Button>
                  ) : null}

                  <Button
                    disabled={disabled}
                    className="opacity-80"
                    danger
                    onClick={handleClearAllProducts}
                  >
                    Clear All Products
                  </Button>
                </div>

                {originalPlan.wisCustomer?.id === "5" && (
                  <div className="mt-2 opacity-70 italic">
                    Only produccts in the WIS price list can be added to the
                    plan. If you can not find the product you want to add,
                    please contact Stella to add the product to the WIS price
                    list first.
                  </div>
                )}
              </td>
            </tr>
          )}

          <tr className="text-base whitespace-nowrap">
            <td className="border-y dark:border-gray-700" colSpan={2}>
              Total
            </td>
            <td className="border-y dark:border-gray-700 text-right">
              {totalQty}
            </td>
            <td className="border-y dark:border-gray-700 text-right">
              {totalCartons}
            </td>
            <td className="border-y dark:border-gray-700 text-right">
              {totalGrossWeight.toFixed(3)}
            </td>
            <td className="border-y dark:border-gray-700 text-right">
              {totalCbm.toFixed(3)}
            </td>
            <td className="border-y dark:border-gray-700 text-right">
              {originalPlan.readyDate
                ? moment(originalPlan.readyDate).format("YYYY-MM-DD")
                : "-"}
            </td>

            {originalPlan.planType === "AB_INTERNAL_TRANSFER" ? (
              <>
                <td className="text-right border-y dark:border-gray-700"></td>
                <td className="text-right border-y dark:border-gray-700">
                  {currencySymbolString} {totalCost.toFixed(2)}
                </td>
              </>
            ) : (
              <>
                <td className="text-right border-y dark:border-gray-700"></td>
                <td className="text-right border-y dark:border-gray-700"></td>
                <td className="text-right border-y dark:border-gray-700">
                  {currencySymbolString}{" "}
                  {totalPrice ? totalPrice.toFixed(2) : "0.00"}
                </td>
              </>
            )}
          </tr>

          {duplicatedProducts.length > 0 ? (
            <tr>
              <td className="text-red-600 font-semibold" colSpan={4}>
                Warning: There are duplicated products in the plan. Make sure
                this is intended. Duplicated products:{" "}
                {duplicatedProducts.map((i) => i.product.number).join(", ")}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
};

export default PlanLines;
